(() => {
  const EVENT_PREFIX = 'the_art_neon:';
  const EVENT_VERSION = '1.0';
  const PENDING_KEY = 'tan.procurement.pending.v1';
  const REPORTED_KEY = 'tan.procurement.reported.v1';
  const ROUTE_EVENT = 'tan:trade-route-selected';
  const SAFE_ROUTE_IDS = new Set(['multi_sign', 'multi_location', 'trade_partner']);
  const SAFE_VARIATIONS = new Set(['same_design', 'controlled_variations', 'different_designs', 'not_sure']);
  const SAFE_DELIVERY = new Set(['one_address', 'multiple_locations', 'multiple_countries', 'not_sure']);
  const SAFE_DESIGN_TYPES = new Set(['approved_artwork', 'text_or_wording', 'concept_only', 'mixed_set']);
  const SAFE_ENVIRONMENTS = new Set(['indoor', 'outdoor', 'mixed', 'not_sure']);
  const emittedOnce = new Set();
  const copyCache = new WeakMap();

  const copyRootFor = (root) => {
    if (root?.matches?.('[data-trade-intake]')) return root;
    const page = root?.closest?.('main') || document;
    return page.querySelector?.('[data-trade-intake]') || document.querySelector('[data-trade-intake]');
  };

  const readCopy = (root) => {
    const copyRoot = copyRootFor(root);
    if (!copyRoot) return {};
    if (copyCache.has(copyRoot)) return copyCache.get(copyRoot);
    let parsed = {};
    try {
      const value = JSON.parse(copyRoot.querySelector('[data-tan-procurement-copy]')?.textContent || '{}');
      if (value && typeof value === 'object' && !Array.isArray(value)) parsed = value;
    } catch (_) {
      parsed = {};
    }
    copyCache.set(copyRoot, parsed);
    return parsed;
  };

  const copyText = (copy, key, fallback = '') => {
    const value = copy?.[key];
    return typeof value === 'string' && value.trim() ? value : fallback;
  };

  const formatCopy = (template, values = {}) => String(template || '').replace(/\{([a-z_]+)\}/gi, (match, key) => (
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match
  ));

  const cleanId = (value, allowed, fallback = 'unselected') => {
    const cleaned = String(value || '').trim().toLowerCase();
    return allowed.has(cleaned) ? cleaned : fallback;
  };

  const successUrlFor = (record, href = window.location.href) => {
    const url = new URL(href, window.location.origin);
    url.searchParams.set('quote_submitted', '1');
    url.searchParams.set('quote_form', 'bulk_quote');
    url.searchParams.set('quote_category', 'bulk');
    url.searchParams.set('quote_route', cleanId(record?.route_id, SAFE_ROUTE_IDS));
    url.hash = '';
    return url;
  };

  const readContext = () => {
    try {
      return JSON.parse(document.getElementById('TanLeadContext')?.textContent || '{}');
    } catch (_) {
      return {};
    }
  };

  const context = readContext();
  const commonPayload = () => ({
    event_version: EVENT_VERSION,
    template: String(context.template_suffix || 'trade').slice(0, 50),
    country_code: String(context.country_code || '').slice(0, 10),
    language_code: String(context.language_code || document.documentElement.lang || '').slice(0, 20),
  });

  const emit = (name, details = {}, onceKey = '') => {
    if (context.design_mode === true) return;
    if (onceKey && emittedOnce.has(onceKey)) return;
    if (onceKey) emittedOnce.add(onceKey);
    const data = { ...commonPayload(), ...details };
    window.dispatchEvent(new CustomEvent('tan:lead-event', { detail: { name, data } }));
    try {
      window.Shopify?.analytics?.publish?.(`${EVENT_PREFIX}${name}`, data);
    } catch (_) {
      // The page and form remain fully usable when analytics is unavailable.
    }
  };

  const storage = {
    get(key) {
      try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch (_) { return null; }
    },
    set(key, value) {
      try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (_) { /* optional */ }
    },
  };

  const initRouter = (root) => {
    if (!root || root.dataset.tradeReady === 'true') return;
    root.dataset.tradeReady = 'true';
    const copy = readCopy(root);
    const cards = Array.from(root.querySelectorAll('[data-trade-route-card]'));
    const output = root.querySelector('[data-trade-route-output]');
    const apply = root.querySelector('[data-trade-route-apply]');
    const templates = Array.from(root.querySelectorAll('template[data-trade-route-template]'));
    let selectedRoute = '';

    const render = (routeId, announce = true) => {
      const safeRoute = cleanId(routeId, SAFE_ROUTE_IDS, '');
      const card = cards.find((item) => item.dataset.routeId === safeRoute);
      const template = templates.find((item) => item.dataset.tradeRouteTemplate === safeRoute);
      if (!card || !template || !output) return false;
      selectedRoute = safeRoute;
      cards.forEach((item) => item.setAttribute('aria-pressed', String(item === card)));
      output.replaceChildren(template.content.cloneNode(true));
      if (apply) {
        apply.dataset.routeId = safeRoute;
        const routeLabel = card.dataset.routeLabel || copyText(copy, 'route_fallback');
        const applyLabel = formatCopy(copyText(copy, 'route_apply_aria'), { route: routeLabel });
        if (applyLabel) apply.setAttribute('aria-label', applyLabel);
      }
      if (announce) {
        emit('procurement_route_selected', { route_id: safeRoute });
      }
      return true;
    };

    cards.forEach((card) => {
      card.addEventListener('click', () => render(card.dataset.routeId));
    });

    apply?.addEventListener('click', () => {
      if (!selectedRoute) return;
      const selectedCard = cards.find((item) => item.dataset.routeId === selectedRoute);
      window.dispatchEvent(new CustomEvent(ROUTE_EVENT, {
        detail: {
          routeId: selectedRoute,
          routeLabel: selectedCard?.dataset.routeLabel || selectedRoute,
        },
      }));
      emit('procurement_route_applied', { route_id: selectedRoute });
    });

    const requested = cleanId(new URLSearchParams(window.location.search).get('trade_route'), SAFE_ROUTE_IDS, '');
    const defaultCard = cards.find((card) => card.dataset.routeDefault === 'true') || cards[0];
    render(requested || defaultCard?.dataset.routeId, false);
  };

  const initForm = (root) => {
    if (!root || root.dataset.tradeFormReady === 'true') return;
    root.dataset.tradeFormReady = 'true';
    const copy = readCopy(root);
    const form = root.querySelector('form');
    if (!form) return;
    form.classList.add('is-enhanced');

    const panels = Array.from(form.querySelectorAll('[data-trade-panel]'));
    const progress = Array.from(form.querySelectorAll('[data-trade-step]'));
    const nextButtons = Array.from(form.querySelectorAll('[data-trade-next]'));
    const backButtons = Array.from(form.querySelectorAll('[data-trade-back]'));
    const summaryField = form.querySelector('[data-trade-summary-field]');
    const bandField = form.querySelector('[data-trade-quantity-band]');
    const readinessField = form.querySelector('[data-trade-readiness-band]');
    const readinessBar = form.querySelector('[data-trade-readiness-bar]');
    const readinessLabel = form.querySelector('[data-trade-readiness-label]');
    const readinessScore = form.querySelector('[data-trade-readiness-score]');
    const summaryNodes = new Map(Array.from(form.querySelectorAll('[data-trade-summary-value]')).map((node) => [node.dataset.tradeSummaryValue, node]));
    const copyButton = form.querySelector('[data-trade-copy]');
    const targetDate = form.querySelector('[name="contact[Target date]"]');
    const uploadSlots = Array.from(form.querySelectorAll('[data-upload-app-slot]'));
    const uploadLinksField = form.querySelector('[data-trade-upload-links]');
    const uploadError = form.querySelector('[data-trade-upload-error]');
    const uploadRecords = new Map();
    const initialNotSelected = summaryNodes.get('route')?.textContent?.trim() || '';
    const initialNotEntered = summaryNodes.get('quantity')?.textContent?.trim() || '';
    const initialReadiness = readinessLabel?.textContent?.trim() || '';
    const notSelected = copyText(copy, 'not_selected', initialNotSelected);
    const notEntered = copyText(copy, 'not_entered', initialNotEntered);
    let activeStep = 1;
    let unlockedStep = 1;

    if (targetDate && !targetDate.min) {
      const now = new Date();
      const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
      targetDate.min = localDate;
    }

    const fieldsByName = (name) => Array.from(form.querySelectorAll(`[name="${name}"]`));
    const fieldValue = (name) => {
      const fields = fieldsByName(name);
      const selected = fields.find((field) => field.type === 'radio' || field.type === 'checkbox' ? field.checked : true);
      return String(selected?.value || '').trim();
    };

    const fieldLabel = (name, empty = notSelected) => {
      const fields = fieldsByName(name);
      const selected = fields.find((field) => field.type === 'radio' || field.type === 'checkbox' ? field.checked : true);
      if (!selected) return empty;
      if (selected.tagName === 'SELECT') return selected.selectedOptions[0]?.textContent?.trim() || empty;
      return selected.dataset.summaryLabel || selected.closest('label')?.querySelector('b')?.textContent?.trim() || selected.value || empty;
    };

    const quantityBand = () => {
      const quantity = Math.max(0, Number(fieldValue('contact[Quantity]')) || 0);
      if (quantity < 2) return 'unselected';
      if (quantity <= 19) return '2_19';
      if (quantity <= 49) return '20_49';
      if (quantity <= 99) return '50_99';
      return '100_plus';
    };

    const quantityBandLabel = (band) => ({
      '2_19': copyText(copy, 'band_2_19'),
      '20_49': copyText(copy, 'band_20_49'),
      '50_99': copyText(copy, 'band_50_99'),
      '100_plus': copyText(copy, 'band_100_plus'),
    }[band] || copyText(copy, 'band_unclassified'));

    const quantitySummary = (quantity, band) => {
      let category = 'other';
      try {
        category = new Intl.PluralRules(context.language_code || document.documentElement.lang || 'en').select(Number(quantity));
      } catch (_) {
        category = Number(quantity) === 1 ? 'one' : 'other';
      }
      const template = copyText(copy, `quantity_${category}`, copyText(copy, 'quantity_other'));
      return formatCopy(template, { count: quantity, band });
    };

    const hasArtworkUpload = () => uploadRecords.size > 0;

    const safeState = () => ({
      route_id: cleanId(fieldValue('contact[Project route]'), SAFE_ROUTE_IDS),
      quantity_band: quantityBand(),
      variation_id: cleanId(fieldValue('contact[Design variation]'), SAFE_VARIATIONS),
      delivery_id: cleanId(fieldValue('contact[Delivery structure]'), SAFE_DELIVERY),
      design_type: cleanId(fieldValue('contact[Design type]'), SAFE_DESIGN_TYPES),
      environment_id: cleanId(fieldValue('contact[Installation environment]'), SAFE_ENVIRONMENTS),
    });

    const readiness = () => {
      const state = safeState();
      const checks = [
        state.route_id !== 'unselected',
        state.quantity_band !== 'unselected',
        state.variation_id !== 'unselected',
        state.delivery_id !== 'unselected',
        state.design_type !== 'unselected',
        state.environment_id !== 'unselected',
        Boolean(fieldValue('contact[Destinations]')),
        Boolean(hasArtworkUpload() || fieldValue('contact[Target date]') || fieldValue('contact[Artwork share link]') || fieldValue('contact[Project notes]')),
      ];
      const completed = checks.filter(Boolean).length;
      const percent = Math.round((completed / checks.length) * 100);
      const band = completed >= 8 ? 'review_ready' : completed >= 5 ? 'scoping_ready' : 'early_direction';
      const label = band === 'review_ready'
        ? copyText(copy, 'readiness_review', initialReadiness)
        : band === 'scoping_ready'
          ? copyText(copy, 'readiness_scoping', initialReadiness)
          : copyText(copy, 'readiness_early', initialReadiness);
      return { completed, percent, band, label };
    };

    const updateSummary = () => {
      const quantity = fieldValue('contact[Quantity]');
      const values = {
        route: fieldLabel('contact[Project route]'),
        quantity: quantity ? quantitySummary(quantity, quantityBandLabel(quantityBand())) : notEntered,
        variation: fieldLabel('contact[Design variation]'),
        delivery: fieldLabel('contact[Delivery structure]'),
        design: fieldLabel('contact[Design type]'),
        environment: fieldLabel('contact[Installation environment]'),
        size: fieldLabel('contact[Approximate size]'),
        target: fieldValue('contact[Target date]') || notEntered,
      };
      summaryNodes.forEach((node, key) => { node.textContent = values[key] || notEntered; });

      const status = readiness();
      if (bandField) bandField.value = quantityBand();
      if (readinessField) readinessField.value = status.band;
      if (readinessBar) readinessBar.style.width = `${status.percent}%`;
      if (readinessLabel) readinessLabel.textContent = status.label;
      if (readinessScore) readinessScore.textContent = `${status.completed}/8`;

      const lines = [
        copyText(copy, 'summary_title'),
        formatCopy(copyText(copy, 'line_project_route'), { value: values.route }),
        formatCopy(copyText(copy, 'line_expected_quantity'), { value: values.quantity }),
        formatCopy(copyText(copy, 'line_design_structure'), { value: values.variation }),
        formatCopy(copyText(copy, 'line_delivery_structure'), { value: values.delivery }),
        formatCopy(copyText(copy, 'line_design_source'), { value: values.design }),
        formatCopy(copyText(copy, 'line_environment'), { value: values.environment }),
        formatCopy(copyText(copy, 'line_approximate_size'), { value: values.size }),
        formatCopy(copyText(copy, 'line_target_date'), { value: values.target }),
        formatCopy(copyText(copy, 'line_brief_readiness'), { value: status.label }),
        copyText(copy, 'summary_boundary'),
      ].filter(Boolean);
      if (summaryField) summaryField.value = lines.join('\n');
      return { values, status, lines };
    };

    const uploadUrlFromDetail = (detail) => {
      const value = String(detail?.url || detail?.file?.url || detail?.meta?.url || '').trim();
      return /^https:\/\//i.test(value) ? value : '';
    };

    const uploadNameFromDetail = (detail) => String(
      detail?.name || detail?.filename || detail?.fileName || detail?.originalName
      || detail?.meta?.name || detail?.meta?.filename || detail?.file?.name || '',
    ).trim();

    const uploadKeyFromDetail = (detail, url, name = '') => String(
      detail?.id || detail?.uuid || detail?.key || detail?.url || url || name,
    ).trim();
    let pendingUploads = 0;

    const syncUploadLinks = () => {
      if (uploadLinksField) {
        uploadLinksField.value = Array.from(uploadRecords.values())
          .map((record) => record.url)
          .filter(Boolean)
          .join('\n');
      }
    };

    const pendingUploadCount = () => Math.max(0, pendingUploads);

    uploadSlots.forEach((slot) => {
      const placeholder = slot.querySelector('[data-upload-lift-placeholder]');
      if (!placeholder) return;
      const syncPlaceholder = () => {
        const hasUploadWidget = Array.from(slot.children).some((child) => child !== placeholder);
        placeholder.hidden = hasUploadWidget;
        updateSummary();
      };
      new MutationObserver(syncPlaceholder).observe(slot, { childList: true });
      slot.addEventListener('change', (event) => {
        if (!event.target?.matches?.('input[type="file"]')) return;
        const selectedCount = event.target.files?.length || 0;
        if (selectedCount > 0) pendingUploads += selectedCount;
      });
      slot.addEventListener('upload:ready', () => {
        placeholder.hidden = true;
        updateSummary();
      });
      slot.addEventListener('upload:added', (event) => {
        const detail = event.detail || {};
        if (detail.meta?.variant === 'thumbnail') return;
        const url = uploadUrlFromDetail(detail);
        if (!url) return;
        const name = uploadNameFromDetail(detail);
        uploadRecords.set(uploadKeyFromDetail(detail, url, name), { name, url });
        pendingUploads = Math.max(0, pendingUploads - 1);
        syncUploadLinks();
        if (uploadError) uploadError.hidden = true;
        updateSummary();
      });
      slot.addEventListener('upload:removed', (event) => {
        const detail = event.detail || {};
        if (detail.meta?.variant === 'thumbnail') return;
        const url = uploadUrlFromDetail(detail);
        const name = uploadNameFromDetail(detail);
        const key = uploadKeyFromDetail(detail, url, name);
        let removedConfirmedUpload = false;
        if (key && uploadRecords.has(key)) removedConfirmedUpload = uploadRecords.delete(key);
        else if (url || name) {
          const match = Array.from(uploadRecords.entries()).find(([, record]) => (
            (url && record.url === url) || (name && record.name === name)
          ));
          if (match) removedConfirmedUpload = uploadRecords.delete(match[0]);
        }
        if (!removedConfirmedUpload) pendingUploads = Math.max(0, pendingUploads - 1);
        syncUploadLinks();
        updateSummary();
      });
      syncPlaceholder();
    });

    form.addEventListener('reset', () => {
      pendingUploads = 0;
      uploadRecords.clear();
      syncUploadLinks();
      updateSummary();
    });

    const showStep = (step, options = {}) => {
      const nextStep = Math.max(1, Math.min(3, Number(step) || 1));
      activeStep = nextStep;
      unlockedStep = Math.max(unlockedStep, nextStep);
      panels.forEach((panel) => { panel.hidden = Number(panel.dataset.tradePanel) !== nextStep; });
      progress.forEach((button) => {
        const isCurrent = Number(button.dataset.tradeStep) === nextStep;
        if (isCurrent) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
        button.setAttribute('aria-disabled', String(Number(button.dataset.tradeStep) > unlockedStep + 1));
      });
      updateSummary();
      if (options.focus !== false) panels.find((panel) => Number(panel.dataset.tradePanel) === nextStep)?.querySelector('h2')?.focus?.({ preventScroll: true });
      if (options.scroll === true) root.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    };

    const firstInvalid = (panel) => Array.from(panel.querySelectorAll('input, select, textarea')).find((field) => !field.disabled && !field.checkValidity());
    const validateStep = (step) => {
      const panel = panels.find((item) => Number(item.dataset.tradePanel) === step);
      if (!panel) return true;
      const invalid = firstInvalid(panel);
      if (!invalid) return true;
      invalid.reportValidity();
      invalid.focus({ preventScroll: true });
      invalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    };

    nextButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const from = Number(button.dataset.tradeNext) || activeStep;
        if (!validateStep(from)) return;
        const state = safeState();
        const status = readiness();
        emit('procurement_step_completed', { step_id: `step_${from}`, readiness_band: status.band, route_id: state.route_id });
        showStep(from + 1, { scroll: true });
      });
    });

    backButtons.forEach((button) => button.addEventListener('click', () => showStep(Number(button.dataset.tradeBack) || activeStep - 1, { scroll: true })));

    progress.forEach((button) => {
      button.addEventListener('click', () => {
        const target = Number(button.dataset.tradeStep) || 1;
        if (target <= unlockedStep) showStep(target, { scroll: true });
        else if (target === activeStep + 1 && validateStep(activeStep)) showStep(target, { scroll: true });
      });
    });

    window.addEventListener(ROUTE_EVENT, (event) => {
      const routeId = cleanId(event.detail?.routeId, SAFE_ROUTE_IDS, '');
      if (!routeId) return;
      const input = fieldsByName('contact[Project route]').find((field) => field.value === routeId);
      if (input) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      showStep(1, { focus: false });
    });

    form.addEventListener('input', updateSummary);
    form.addEventListener('change', updateSummary);
    form.addEventListener('invalid', (event) => {
      const panel = event.target.closest('[data-trade-panel]');
      if (panel) showStep(Number(panel.dataset.tradePanel), { focus: false });
    }, true);

    copyButton?.addEventListener('click', async () => {
      const summary = updateSummary();
      try {
        await navigator.clipboard.writeText(summary.lines.join('\n'));
        const original = copyButton.textContent;
        copyButton.textContent = copyButton.dataset.copiedLabel || copyText(copy, 'copied', original);
        window.setTimeout(() => { copyButton.textContent = original; }, 1800);
        emit('procurement_summary_copied', { readiness_band: summary.status.band, route_id: safeState().route_id });
      } catch (_) {
        copyButton.textContent = copyButton.dataset.copyFailedLabel || copyText(copy, 'copy_failed', copyButton.textContent);
      }
    });

    form.addEventListener('submit', (event) => {
      if (pendingUploadCount() > 0) {
        event.preventDefault();
        if (uploadError) {
          uploadError.textContent = copyText(copy, 'upload_wait', uploadError.textContent);
          uploadError.hidden = false;
          uploadError.focus({ preventScroll: true });
          uploadError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
      syncUploadLinks();
      const summary = updateSummary();
      const state = safeState();
      const pending = {
        submitted_at: Date.now(),
        ...state,
        quantity_band: quantityBand(),
        readiness_band: summary.status.band,
        has_artwork_upload: hasArtworkUpload(),
        has_artwork_link: Boolean(fieldValue('contact[Artwork share link]')),
        target_date_provided: Boolean(fieldValue('contact[Target date]')),
      };
      storage.set(PENDING_KEY, pending);
      emit('procurement_form_submit_attempted', pending, `procurement_submit:${pending.submitted_at}`);
    });

    const success = form.querySelector('.tan-trade-form__notice--success');
    const pending = storage.get(PENDING_KEY);
    const reported = storage.get(REPORTED_KEY);
    if (success) {
      const successUrl = successUrlFor(pending);
      try { window.history.replaceState(window.history.state, '', `${successUrl.pathname}${successUrl.search}`); } catch (_) { /* The confirmed success remains valid. */ }
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          success.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'nearest' });
          success.focus({ preventScroll: true });
        });
      });
      if (pending?.submitted_at && pending.submitted_at !== reported?.submitted_at) {
        const safePayload = {
          route_id: cleanId(pending.route_id, SAFE_ROUTE_IDS),
          quantity_band: String(pending.quantity_band || 'unselected').slice(0, 20),
          variation_id: cleanId(pending.variation_id, SAFE_VARIATIONS),
          delivery_id: cleanId(pending.delivery_id, SAFE_DELIVERY),
          design_type: cleanId(pending.design_type, SAFE_DESIGN_TYPES),
          environment_id: cleanId(pending.environment_id, SAFE_ENVIRONMENTS),
          readiness_band: ['early_direction', 'scoping_ready', 'review_ready'].includes(pending.readiness_band) ? pending.readiness_band : 'unknown',
          has_artwork_upload: Boolean(pending.has_artwork_upload),
          has_artwork_link: Boolean(pending.has_artwork_link),
          target_date_provided: Boolean(pending.target_date_provided),
          quote_form: 'bulk_quote',
          success_path: `${successUrl.pathname}${successUrl.search}`,
        };
        emit('procurement_quote_submitted', safePayload, `procurement_submitted:${pending.submitted_at}`);
        storage.set(REPORTED_KEY, { submitted_at: pending.submitted_at });
      }
    }

    showStep(1, { focus: false });
  };

  const initAll = (scope = document) => {
    const routers = [];
    const forms = [];
    if (scope.matches?.('[data-trade-router]')) routers.push(scope);
    if (scope.matches?.('[data-trade-intake]')) forms.push(scope);
    scope.querySelectorAll?.('[data-trade-router]').forEach((root) => routers.push(root));
    scope.querySelectorAll?.('[data-trade-intake]').forEach((root) => forms.push(root));
    routers.forEach(initRouter);
    forms.forEach(initForm);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
